import React, { useState, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './components/Dashboard';
import { Calendar } from './components/Calendar';
import { Calculator } from './components/Calculator';
import { Companies } from './components/Companies';
import { Expedition } from './components/Expedition';
import { Auth } from './components/Auth';
import { supabase } from './lib/supabase';
import type { Task } from './types';

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [session, setSession] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(!!session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      fetchTasks();
    }
  }, [session]);

  const fetchTasks = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: tasksData, error } = await supabase
        .from('tasks')
        .select(`
          *,
          companies (
            name
          )
        `)
        .eq('user_id', user.id)
        .order('follow_up_date', { ascending: true });

      if (error) throw error;

      const formattedTasks: Task[] = tasksData.map(task => ({
        id: task.id,
        companyId: task.company_id,
        companyName: task.companies.name,
        comment: task.comment,
        followUpDate: task.follow_up_date,
        createdAt: task.created_at,
        status: task.status
      }));

      setTasks(formattedTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Indlæser...</div>
      </div>
    );
  }

  if (!session) {
    return <Auth />;
  }

  return (
    <div className="flex">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="flex-1 bg-gray-100 min-h-screen">
        {activeTab === 'dashboard' && <Dashboard />}
        {activeTab === 'calendar' && <Calendar tasks={tasks} onTaskCreated={fetchTasks} />}
        {activeTab === 'companies' && <Companies onTaskCreated={fetchTasks} />}
        {activeTab === 'calculator' && <Calculator />}
        {activeTab === 'expedition' && <Expedition />}
      </main>
    </div>
  );
}

export default App;